import { router, usePage } from "@inertiajs/react"
import { useCallback, useMemo } from "react"
import { toast } from "sonner"
import {
  EntityScaleSheetModal,
  type EntityScaleSheetHeightUpdate,
  type EntityScaleSheetLoadResult,
} from "@/components/scale-sheet/entity-scale-sheet-modal"
import { useTranslation } from "@/hooks/use-translation"
import { generateCharacterScalePose } from "@/lib/characters-api"
import { characterScalePoseBusy } from "@/lib/character-jobs"
import { fetchScaleSheet, patchCharacterHeights } from "@/lib/scale-sheet-api"
import { parseScaleSheetEntityKey } from "@/lib/scale-sheet-layout"
import type { WorkspaceBaseProps } from "@/types/workspace"

type Props = {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  characterIds?: number[]
  shotId?: number
  variant?: "project" | "shot"
  closeOnApply?: boolean
  onApplied?: () => void
  showExportActions?: boolean
}

async function loadCharacterScaleSheet(
  projectId: number,
  entityIds: number[] | undefined,
  options: { preview: boolean; shotId?: number; editOnly: boolean },
): Promise<EntityScaleSheetLoadResult> {
  const res = await fetchScaleSheet(projectId, entityIds, options)
  return {
    status: res.status,
    idsKey: res.character_ids_key,
    image_url: res.image_url,
    image_download_url: res.image_download_url,
    layout_meta: res.layout_meta,
    rows: res.characters.map((c) => ({ ...c, entity_type: "character" as const })),
  }
}

async function patchCharacterScaleHeights(projectId: number, updates: EntityScaleSheetHeightUpdate[]) {
  await patchCharacterHeights(
    projectId,
    updates.map((u) => ({ character_id: u.id, height_cm: u.height_cm })),
  )
}

export function ProjectScaleSheetModal({
  projectId,
  open,
  onOpenChange,
  characterIds,
  shotId,
  variant = "project",
  closeOnApply = variant === "project",
  onApplied,
  showExportActions,
}: Props) {
  const { t } = useTranslation(["workspace"])
  const { jobs = [] } = usePage<WorkspaceBaseProps>().props

  const labels = useMemo(
    () => ({
      close: t("charactersPage.close"),
      title: t("charactersPage.scaleSheetTitle"),
      previewTitle: t("charactersPage.scaleSheetPreviewTitle"),
      hint: t("charactersPage.scaleSheetHint"),
      needTwo: t("charactersPage.scaleSheetNeedTwo"),
      applyAll: t("charactersPage.scaleSheetApplyAll"),
    }),
    [t],
  )

  const regenerateFigureTitle = t("charactersPage.regenerateScalePose")
  const figureGenerateDisabledReason = t("charactersPage.scalePoseCharacterSheetRequired")

  const isFigureBusy = useCallback(
    (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed?.entity_type !== "character") return false
      return characterScalePoseBusy(jobs, parsed.id)
    },
    [jobs],
  )

  const handleRegenerateFigure = useCallback(
    async (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed?.entity_type !== "character") return
      try {
        await generateCharacterScalePose(projectId, parsed.id)
        router.reload({ only: ["jobs"] })
      } catch (e) {
        toast.error(e instanceof Error ? e.message : String(e))
        throw e
      }
    },
    [projectId],
  )

  return (
    <EntityScaleSheetModal
      projectId={projectId}
      open={open}
      onOpenChange={onOpenChange}
      entityIds={characterIds}
      shotId={shotId}
      variant={variant}
      closeOnApply={closeOnApply}
      onApplied={onApplied}
      showExportActions={showExportActions}
      loadSheet={loadCharacterScaleSheet}
      patchHeights={patchCharacterScaleHeights}
      labels={labels}
      waitingStatuses={["waiting_pose"]}
      exportFilenamePrefix="scale-sheet"
      minEntities={1}
      figureEntityType="character"
      onRegenerateFigure={handleRegenerateFigure}
      regenerateFigureTitle={regenerateFigureTitle}
      isFigureBusy={isFigureBusy}
      figureGenerateDisabledReason={figureGenerateDisabledReason}
    />
  )
}
